<div class="max-w-7xl mx-auto">
  <h1 class="text-center font-medium text-gray-800">Consensus Workshop</h1>

  <div class="border-2 border-gray-300 text-gray-200 p-4 rounded mt-6">
    <h2 class="mb-2 text-base text-gray-800">Consensus System for Researchers</h2>
    <p class="text-lg text-gray-700 leading-relaxed text-sm">
      The core idea behind having a consensus mediation system like CENTINELA stems from the needs
      of researchers and academics to streamline their decision-making processes. CENTINELA acts as
      a facilitator, guiding research teams in reaching an agreement on which research topic work in
      group. The system generates a curated list of five potential research topics, allowing users
      to engage interactively with each option. Through this collaborative interaction, the platform
      helps researchers converge on the most suitable topic that aligns with the interests and
      expertise of the group, making the decision-making process more efficient and focused.
    </p>
  </div>

  <div class="bg-gray-200 text-gray-200 p-4 rounded mt-6">
    <h2 class="mb-2 text-base text-gray-800">What is consensus workshop?</h2>
    <p class="text-lg text-gray-700 leading-relaxed text-sm">
      It is a creative planning method, useful for creating action plans, making decisions, and
      generating options in both normal and conflict situations. It allows the collection of ideas,
      the establishment of general guidelines, the incorporation of different perspectives, and the
      achievement of a consensual solution among the parties.
    </p>
  </div>

  <div
    class="relative flex flex-col md:flex-row w-full my-6 bg-white shadow-sm border-2 border-gray-300 rounded-lg w-96"
  >
    <div class="relative p-2.5 md:w-2/5 shrink-0 overflow-hidden">
      <img
        src="/assets/concensus.png"
        alt="card-image"
        class="h-full w-full rounded-md md:rounded-lg object-contain"
      />
    </div>
    <div class="p-6">
      <!-- <div class="mb-4 rounded-full bg-primary_tailwind py-0.5 border border-transparent text-white transition-all shadow-sm w-20 text-center">NOVEL</div> -->
      <h2 class="mb-2 text-base text-gray-800">CENTINELA on consensus workshop</h2>
      <p class="text-lg text-gray-700 leading-relaxed text-sm">
        It was developed in three distinct phases, each designed to streamline the decision-making
        process.
      </p>
      <p class="text-lg text-gray-700 leading-relaxed text-sm">
        <span class="font-semibold">Phase One: </span>
        Its purpose is to familiarize participants with the proposed research topics and expand
        their understanding of the available options.
      </p>
      <p class="text-lg text-gray-700 leading-relaxed text-sm">
        <span class="font-semibold">Phase Two: </span>
        Here, the group members actively engage with the topics, ranking them according to their
        personal interests and preferences.
      </p>
      <p class="text-lg text-gray-700 leading-relaxed text-sm">
        <span class="font-semibold">Phase Tree: </span>
        The system presents the consensus results based on the researchers' rankings and
        preferences.
      </p>
    </div>
  </div>

  <div class="bg-gray-200 text-gray-200 p-4 rounded mt-6">
    <h2 class="mb-2 text-base text-gray-800">Phase 1 : Presentation</h2>
    <p class="text-lg text-gray-700 leading-relaxed text-sm">
      In the first phase, once the group is formed, the system assigns five research topics for the
      group to explore. The goal of this phase is to help users become familiar with the topics they
      may work on collaboratively. This ensures that all group members have a clear understanding of
      the available options before proceeding to the next stages of the consensus process.
    </p>
    <p class="text-lg text-gray-700 leading-relaxed text-sm">
      <span class="svgs">
        <svg
          fill="primary_tailwind "
          width="18px"
          height="18px"
          viewBox="0 0 32 32"
          xmlns="http://www.w3.org/2000/svg"
          class="primary_tailwind"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M14.573 2.729c-0.729 0.484-4.292 2.849-7.917 5.255s-6.589 4.396-6.589 4.422c0 0.026 0.182 0.146 0.406 0.266 0.224 0.13 3.797 2.109 7.953 4.411l7.542 4.193 0.193-0.099c0.109-0.052 2.891-1.641 6.188-3.521l5.99-3.427 0.036 10.599h3.557v-12.401l-4.615-3.094c-6.219-4.167-11.188-7.448-11.307-7.474-0.063-0.010-0.703 0.38-1.438 0.87zM7.141 22.177l0.016 2.672 8.828 5.292 8.891-5.339v-2.641c0-1.458-0.016-2.646-0.031-2.646-0.021 0-1.76 1.042-3.87 2.323l-4.406 2.661-0.578 0.339-1.755-1.052c-1.464-0.875-2.927-1.755-4.385-2.641l-2.672-1.615c-0.031-0.010-0.042 1.177-0.036 2.646z"
          />
        </svg>
      </span>
      <span class="font-semibold">Topic Exploration via Google Scholar:</span>
      Users can explore more information about each topic by being redirected to Google Scholar,
      allowing them to gain a deeper understanding of the research areas.
    </p>

    <div class="grid-cols-1 sm:grid md:grid-cols-2 mb-3 gap-3">
      <div class="flex flex-col self-start rounded-lg bg-white sm:shrink-0 sm:grow sm:basis-0">
        <img
          class="rounded-t-lg"
          src="assets\img_notification\interaction_search.png"
          alt="Hollywood Sign on The Hill"
        />

        <div class="p-2">
          <p class="text-lg text-gray-700 leading-relaxed text-sm text-center m-0">
            Interaction with Search Topics
          </p>
        </div>
      </div>

      <div
        class="flex flex-col self-start rounded-lg bg-white shadow-secondary-1 sm:shrink-0 sm:grow sm:basis-0"
      >
        <img
          class="rounded-t-lg object-contain"
          src="assets\img_notification\notification_search.png"
          alt="Palm Springs Road"
        />
        <div class="p-2">
          <p class="text-lg text-gray-700 leading-relaxed text-sm text-center m-0">
            Notification for Search Topic
          </p>
        </div>
      </div>
    </div>

    <p class="text-lg text-gray-700 leading-relaxed text-sm">
      <span class="svgs">
        <svg
          width="18px"
          height="18px"
          viewBox="0 0 48 48"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="svg-icon"
        >
          <rect width="48" height="48" fill="black" fill-opacity="0.01" />
          <path
            class="svg-path"
            d="M19.036 44.0001C18.0561 40.8045 16.5778 38.4222 14.6011 36.8532C11.636 34.4997 6.92483 35.9624 5.18458 33.5349C3.44433 31.1073 6.40382 26.6431 7.44234 24.009C8.48086 21.375 3.46179 20.4436 4.04776 19.6958C4.43842 19.1973 6.97471 17.7587 11.6567 15.3801C12.987 7.79346 17.9008 4.00014 26.3982 4.00014C39.1441 4.00014 44 14.8061 44 21.679C44 28.552 38.1201 35.9563 29.7441 37.5528C28.9951 38.6436 30.0754 40.7927 32.9848 44.0001"
            stroke="#1E3C8B"
            stroke-width="4.66685"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            class="svg-path"
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M19.4997 14.5C18.8464 17.0342 19.0408 18.8138 20.0829 19.8385C21.125 20.8633 22.9011 21.5334 25.4112 21.8489C24.8417 25.1176 25.5361 26.6511 27.4942 26.4493C29.4524 26.2475 30.6289 25.4338 31.0239 24.0083C34.0842 24.8684 35.7428 24.1486 35.9997 21.8489C36.3852 18.3993 34.525 15.6475 33.7624 15.6475C32.9997 15.6475 31.0239 15.5547 31.0239 14.5C31.0239 13.4452 28.7159 12.8493 26.6329 12.8493C24.5499 12.8493 25.8035 11.4452 22.9432 12C21.0363 12.3698 19.8885 13.2031 19.4997 14.5Z"
            fill="#2F88FF"
            stroke="#1E3C8B"
            stroke-width="4"
            stroke-linejoin="round"
          />
          <path
            class="svg-path"
            d="M30.5002 25.5001C29.4833 26.1311 28.0878 27.1804 27.5002 28.0001C26.0313 30.0496 24.8398 31.2975 24.5791 32.6082"
            stroke="#1E3C8B"
            stroke-width="4"
            stroke-linecap="round"
          />
        </svg>
      </span>
      <span class="font-semibold">Expertise Selection:</span>
      The system allows users to select their level of expertise for each topic, categorized as
      junior, intermediate, or expert, which helps guide the group's decision-making.
    </p>

    <div class="grid-cols-1 sm:grid md:grid-cols-2 mb-3 gap-3">
      <div class="flex flex-col self-start rounded-lg bg-white sm:shrink-0 sm:grow sm:basis-0">
        <img
          class="rounded-t-lg"
          src="assets\img_notification\interaction_expertise.png"
          alt="Hollywood Sign on The Hill"
        />

        <div class="p-2">
          <p class="text-lg text-gray-700 leading-relaxed text-sm text-center m-0">
            Interaction with Select Expertise
          </p>
        </div>
      </div>

      <div
        class="flex flex-col self-start rounded-lg bg-white shadow-secondary-1 sm:shrink-0 sm:grow sm:basis-0"
      >
        <img
          class="rounded-t-lg object-contain"
          src="assets\img_notification\notification_expertise.png"
          alt="Palm Springs Road"
        />
        <div class="p-2">
          <p class="text-lg text-gray-700 leading-relaxed text-sm text-center m-0">
            Notification for Expertise Selection
          </p>
        </div>
      </div>
    </div>

    <p class="text-lg text-gray-700 leading-relaxed text-sm">
      <span class="svgs">
        <svg
          fill="black"
          width="18px"
          height="18px"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M2 7a1 1 0 0 1 1-1h18a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1zm0 5a1 1 0 0 1 1-1h12a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1zm1 4a1 1 0 1 0 0 2h9a1 1 0 1 0 0-2H3zm12 1a1 1 0 0 1 1-1h2v-2a1 1 0 1 1 2 0v2h2a1 1 0 1 1 0 2h-2v2a1 1 0 1 1-2 0v-2h-2a1 1 0 0 1-1-1z"
          />
        </svg>
      </span>
      <span class="font-semibold">Topic Suggestion:</span>
      Users are given the option to propose one additional research topic, limited to one per
      person, to prevent overloading the list of available topics.
    </p>

    <div class="grid-cols-1 sm:grid md:grid-cols-2 gap-3">
      <div class="flex flex-col self-start rounded-lg bg-white sm:shrink-0 sm:grow sm:basis-0">
        <img
          class="rounded-t-lg"
          src="assets\img_notification\interaction_add.png"
          alt="Hollywood Sign on The Hill"
        />

        <div class="p-2">
          <p class="text-lg text-gray-700 leading-relaxed text-sm text-center m-0">
            Interaction with Add Topic
          </p>
        </div>
      </div>

      <div
        class="flex flex-col self-start rounded-lg bg-white shadow-secondary-1 sm:shrink-0 sm:grow sm:basis-0"
      >
        <img
          class="rounded-t-lg object-contain"
          src="assets\img_notification\notification_add.png"
          alt="Palm Springs Road"
        />
        <div class="p-2">
          <p class="text-lg text-gray-700 leading-relaxed text-sm text-center m-0">
            Notification for Add Topic
          </p>
        </div>
      </div>
    </div>
  </div>

  <div class="border-2 border-gray-300 text-gray-200 p-4 rounded mt-6">
    <h2 class="mb-2 text-base text-gray-800">Phase 2 : Valuation</h2>
    <p class="text-lg text-gray-700 leading-relaxed text-sm">
      The second phase focuses on allowing each user to re-rank the research topics presented in
      Phase One based on their individual preferences. This phase ensures that every group member
      can express their priorities, which helps guide the overall consensus-building process. Users
      organize the topics in order of interest, with the top position representing the topic that
      most appeals to them.
    </p>

    <p class="text-lg text-gray-700 leading-relaxed text-sm">
      <span class="svgs">
        <svg
          width="18px"
          height="18px"
          viewBox="0 0 1024 1024"
          class="icon"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="#000000"
            d="M186.496 544l41.408 41.344a32 32 0 11-45.248 45.312l-96-96a32 32 0 010-45.312l96-96a32 32 0 1145.248 45.312L186.496 480h290.816V186.432l-41.472 41.472a32 32 0 11-45.248-45.184l96-96.128a32 32 0 0145.312 0l96 96.064a32 32 0 01-45.248 45.184l-41.344-41.28V480H832l-41.344-41.344a32 32 0 0145.248-45.312l96 96a32 32 0 010 45.312l-96 96a32 32 0 01-45.248-45.312L832 544H541.312v293.44l41.344-41.28a32 32 0 1145.248 45.248l-96 96a32 32 0 01-45.312 0l-96-96a32 32 0 1145.312-45.248l41.408 41.408V544H186.496z"
          />
        </svg>
      </span>
      <span class="font-semibold">Re-Ranking of Topics:</span>
      Users are tasked with ranking the five research topics according to their personal
      preferences. The first position is reserved for the topic that interests the user the most,
      with the remaining topics ranked in descending order of interest.
    </p>

    <div class="grid-cols-1 sm:grid md:grid-cols-2 mb-3 gap-3">
      <div
        class="border-2 border-gray-300 flex flex-col self-start rounded-lg bg-white sm:shrink-0 sm:grow sm:basis-0"
      >
        <img
          class="rounded-t-lg"
          src="assets\img_notification\interaction_drag.png"
          alt="Hollywood Sign on The Hill"
        />

        <div class="p-2">
          <p class="text-lg text-gray-700 leading-relaxed text-sm text-center m-0">
            Interaction with Drag and Drop Topics
          </p>
        </div>
      </div>

      <div
        class="border-2 border-gray-300 flex flex-col self-start rounded-lg bg-white shadow-secondary-1 sm:shrink-0 sm:grow sm:basis-0"
      >
        <img
          class="rounded-t-lg object-contain"
          src="assets\img_notification\notification_drag.png"
          alt="Palm Springs Road"
        />
        <div class="p-2">
          <p class="text-lg text-gray-700 leading-relaxed text-sm text-center m-0">
            Notification for Drag and Drop Topics
          </p>
        </div>
      </div>
    </div>

    <p class="text-lg text-gray-700 leading-relaxed text-sm">
      <span class="svgs">
        <svg
          version="1.1"
          id="Uploaded to svgrepo.com"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          width="18px"
          height="18px"
          viewBox="0 0 32 32"
          xml:space="preserve"
        >
          <style type="text/css">
            .afiado_een {
              fill: #0b1719;
            }
          </style>
          <path class="afiado_een" d="M29,3H17.7L2.8,17.8l11.3,11.3L29,14.3V3z M20,12V8h4v4H20z" />
        </svg>
      </span>
      <span class="font-semibold">Topic Tagging with Predefined Labels:</span>
      Users can label each topic with one of five predefined tags to further categorize the topics.
      Three tags are positive Novel, Attractive, and Trending while two are negative Obsolete and
      Unfamiliar. When a topic is labeled Novel, it is automatically moved to the top of the list,
      while a topic tagged as Obsolete is placed at the bottom.
    </p>

    <div class="grid-cols-1 sm:grid md:grid-cols-2 gap-3">
      <div
        class="border-2 border-gray-300 flex flex-col self-start rounded-lg bg-white sm:shrink-0 sm:grow sm:basis-0"
      >
        <img
          class="rounded-t-lg"
          src="assets\img_notification\interaction_label.png"
          alt="Hollywood Sign on The Hill"
        />

        <div class="p-2">
          <p class="text-lg text-gray-700 leading-relaxed text-sm text-center m-0">
            Interaction with Label Topics
          </p>
        </div>
      </div>

      <div
        class="border-2 border-gray-300 flex flex-col self-start rounded-lg bg-white shadow-secondary-1 sm:shrink-0 sm:grow sm:basis-0"
      >
        <img
          class="rounded-t-lg object-contain"
          src="assets\img_notification\notification_label.png"
          alt="Palm Springs Road"
        />
        <div class="p-2">
          <p class="text-lg text-gray-700 leading-relaxed text-sm text-center m-0">
            Notification for Label Topics
          </p>
        </div>
      </div>
    </div>
  </div>

  <div class="bg-gray-200 text-gray-200 p-4 rounded mt-6">
    <h2 class="mb-2 text-base text-gray-800">Phase 3 : Decision</h2>
    <p class="text-lg text-gray-700 leading-relaxed text-sm">
      In the final phase, the results of the consensus are presented to the group in a podium
      format, where the top-ranked research topic is awarded the "gold medal," signifying that it is
      the most suitable topic for the team to pursue.
    </p>
    <p class="text-lg text-gray-700 leading-relaxed text-sm">
      <span class="svgs">
        <svg
          fill="#000000"
          width="18px"
          height="18px"
          viewBox="0 0 32 32"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
        >
          <title>podium</title>
          <path
            d="M5.648 24.339h2.402v1.035h-3.967v-1.035l1.993-1.759c0.178-0.161 0.31-0.318 0.395-0.472s0.128-0.313 0.128-0.479c0-0.256-0.087-0.462-0.26-0.618-0.171-0.156-0.399-0.234-0.684-0.234-0.219 0-0.459 0.048-0.72 0.143-0.261 0.093-0.54 0.232-0.837 0.417v-1.199c0.317-0.105 0.63-0.184 0.94-0.238 0.31-0.056 0.613-0.084 0.91-0.084 0.653 0 1.16 0.144 1.521 0.431 0.363 0.288 0.545 0.689 0.545 1.203 0 0.297-0.077 0.575-0.23 0.834-0.154 0.256-0.476 0.6-0.969 1.031l-1.166 1.024zM27.449 26.392c0.368 0.095 0.647 0.261 0.837 0.497 0.193 0.234 0.289 0.533 0.289 0.896 0 0.541-0.207 0.953-0.622 1.236-0.414 0.28-1.019 0.42-1.813 0.42-0.28 0-0.562-0.023-0.845-0.069-0.28-0.044-0.558-0.111-0.834-0.201v-1.086c0.263 0.132 0.524 0.232 0.782 0.3 0.261 0.066 0.517 0.099 0.768 0.099 0.373 0 0.658-0.065 0.856-0.194 0.2-0.129 0.3-0.314 0.3-0.556 0-0.249-0.102-0.436-0.307-0.563-0.202-0.129-0.502-0.194-0.899-0.194h-0.563v-0.907h0.592c0.353 0 0.617-0.055 0.79-0.165 0.173-0.112 0.26-0.282 0.26-0.508 0-0.21-0.084-0.372-0.252-0.486s-0.406-0.172-0.713-0.172c-0.227 0-0.456 0.026-0.687 0.077s-0.462 0.127-0.691 0.227v-1.031c0.278-0.078 0.553-0.137 0.826-0.175s0.541-0.058 0.804-0.059c0.709 0 1.239 0.117 1.59 0.351 0.353 0.232 0.53 0.581 0.53 1.049 0 0.319-0.084 0.581-0.252 0.786-0.168 0.202-0.417 0.345-0.746 0.428zM14.73 14.397h1.243v-3.528l-1.276 0.263v-0.958l1.269-0.263h1.338v4.486h1.243v0.973h-3.817v-0.972zM30.949 30.949v-8.985h-8.985v-13.977h-10.982v9.984h-9.984v12.979z"
          ></path>
        </svg>
      </span>
      <span class="font-semibold">Podium Display of Results:</span>
      The research topic ranked highest by the group is placed at the top, symbolized with a gold
      medal, followed by the second and third-ranked topics. This visual hierarchy helps the group
      quickly identify the most favored research direction based on their collective input
    </p>

    <div class="grid-cols-1 sm:grid mb-3">
      <div class="mx-auto flex flex-col items-center rounded-lg bg-white" style="width: auto">
        <div class="flex justify-center">
          <img
            class="rounded-t-lg object-contain"
            src="assets/img_notification/podium.png"
            alt="Hollywood Sign on The Hill"
          />
        </div>
        <div class="p-2 w-full text-center">
          <p class="text-lg text-gray-700 leading-relaxed text-sm m-0">Result of consensus</p>
        </div>
      </div>
    </div>

    <p class="text-lg text-gray-700 leading-relaxed text-sm">
      <span class="svgs">
        <svg
          width="18px"
          height="18px"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="12" r="10" stroke="#33363F" stroke-width="2" stroke-linecap="round" />
          <path
            d="M7.88124 15.7559C8.37391 16.1826 9.02309 16.4909 9.72265 16.6928C10.4301 16.897 11.2142 17 12 17C12.7858 17 13.5699 16.897 14.2774 16.6928C14.9769 16.4909 15.6261 16.1826 16.1188 15.7559"
            stroke="#33363F"
            stroke-width="2"
            stroke-linecap="round"
          />
          <circle
            cx="9"
            cy="10"
            r="1.25"
            fill="#33363F"
            stroke="#33363F"
            stroke-width="0.5"
            stroke-linecap="round"
          />
          <circle
            cx="15"
            cy="10"
            r="1.25"
            fill="#33363F"
            stroke="#33363F"
            stroke-width="0.5"
            stroke-linecap="round"
          />
        </svg>
      </span>
      <span class="font-semibold">Group Satisfaction Rating:</span>
      After the final results are presented, group members are invited to rate their satisfaction
      with the outcome of the consensus process.
    </p>

    <div class="grid-cols-1 sm:grid md:grid-cols-2 mb-3 gap-3">
      <div class="flex flex-col self-start rounded-lg bg-white sm:shrink-0 sm:grow sm:basis-0">
        <img
          class="rounded-t-lg"
          src="assets\img_notification\interaction_vote.png"
          alt="Hollywood Sign on The Hill"
        />

        <div class="p-2">
          <p class="text-lg text-gray-700 leading-relaxed text-sm text-center m-0">
            Interaction with Satisfaction Rating
          </p>
        </div>
      </div>

      <div
        class="flex flex-col self-start rounded-lg bg-white shadow-secondary-1 sm:shrink-0 sm:grow sm:basis-0"
      >
        <img
          class="rounded-t-lg object-contain"
          src="assets\img_notification\notification_vote.png"
          alt="Palm Springs Road"
        />
        <div class="p-2">
          <p class="text-lg text-gray-700 leading-relaxed text-sm text-center m-0">
            Notification for Satisfaction Rating
          </p>
        </div>
      </div>
    </div>
  </div>

  <div class="border-2 border-gray-300 text-gray-200 p-4 rounded mt-6">
    <h2 class="mb-2 text-lg text-gray-800">Algorithms for Consensus Workshop</h2>

    <!-- Positional Voting Algorithm -->
    <div class="mb-4">
      <p>
        <span class="svgs">
          <svg
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.3501 14.7003C1.9376 14.7003 1.5846 14.5535 1.2911 14.26C0.997598 13.9665 0.850598 13.6133 0.850098 13.2003V10.3503C0.850098 10.1753 0.881348 10.0003 0.943848 9.82529C1.00635 9.65029 1.1001 9.49404 1.2251 9.35654L2.3876 8.04404C2.5251 7.88154 2.70335 7.79729 2.92235 7.79129C3.14135 7.78529 3.3256 7.85704 3.4751 8.00654C3.6126 8.14404 3.6876 8.31279 3.7001 8.51279C3.7126 8.71279 3.6501 8.88779 3.5126 9.03779L2.48135 10.2003H12.7188L11.7251 9.07529C11.5876 8.92529 11.5251 8.75029 11.5376 8.55029C11.5501 8.35029 11.6251 8.18154 11.7626 8.04404C11.9126 7.89404 12.0971 7.82204 12.3161 7.82804C12.5351 7.83404 12.7131 7.91854 12.8501 8.08154L13.9751 9.35654C14.1001 9.49404 14.1938 9.65029 14.2563 9.82529C14.3188 10.0003 14.3501 10.1753 14.3501 10.3503V13.2003C14.3501 13.6128 14.2033 13.966 13.9098 14.26C13.6163 14.554 13.2631 14.7008 12.8501 14.7003H2.3501ZM6.5501 9.00029L3.94385 6.35654C3.65635 6.06904 3.5126 5.71279 3.5126 5.28779C3.5126 4.86279 3.65635 4.50654 3.94385 4.21904L7.61885 0.544043C7.90635 0.256543 8.2626 0.112793 8.6876 0.112793C9.1126 0.112793 9.46885 0.256543 9.75635 0.544043L12.4001 3.15029C12.6876 3.43779 12.8346 3.79079 12.8411 4.20929C12.8476 4.62779 12.7068 4.98104 12.4188 5.26904L8.66885 9.01904C8.38135 9.30654 8.02835 9.44729 7.60985 9.44129C7.19135 9.43529 6.8381 9.28829 6.5501 9.00029ZM11.3501 4.20029L8.6876 1.57529L4.9751 5.28779L7.6376 7.91279L11.3501 4.20029Z"
              fill="#000000"
            />
          </svg>
        </span>
        <span class="font-semibold text-lg text-gray-700">Positional Algorithms:</span>
      </p>
      <p class="text-base text-gray-700 leading-relaxed">
        Positional algorithms assign scores to each option based on their ranking in voters'
        preferences. Options ranked higher receive more points. The result is a weighted average
        influenced by participants' expertise levels.
      </p>
      <p class="text-base text-gray-700 leading-relaxed mt-2">
        <span class="font-semibold">Formula:</span><br />
        <code>
          Weighted Score = Σ (Position<sub>user</sub> × Expertise<sub>user</sub>) / Σ
          (Expertise<sub>user</sub>)
        </code>
      </p>
      <p class="text-base text-gray-700 leading-relaxed mt-2">
        This formula ensures that participants with higher expertise have a proportionally greater
        influence on the final decision.
      </p>
    </div>

    <!-- Schulze Method -->
    <div class="mb-4">
      <p>
        <span class="svgs">
          <svg
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.3501 14.7003C1.9376 14.7003 1.5846 14.5535 1.2911 14.26C0.997598 13.9665 0.850598 13.6133 0.850098 13.2003V10.3503C0.850098 10.1753 0.881348 10.0003 0.943848 9.82529C1.00635 9.65029 1.1001 9.49404 1.2251 9.35654L2.3876 8.04404C2.5251 7.88154 2.70335 7.79729 2.92235 7.79129C3.14135 7.78529 3.3256 7.85704 3.4751 8.00654C3.6126 8.14404 3.6876 8.31279 3.7001 8.51279C3.7126 8.71279 3.6501 8.88779 3.5126 9.03779L2.48135 10.2003H12.7188L11.7251 9.07529C11.5876 8.92529 11.5251 8.75029 11.5376 8.55029C11.5501 8.35029 11.6251 8.18154 11.7626 8.04404C11.9126 7.89404 12.0971 7.82204 12.3161 7.82804C12.5351 7.83404 12.7131 7.91854 12.8501 8.08154L13.9751 9.35654C14.1001 9.49404 14.1938 9.65029 14.2563 9.82529C14.3188 10.0003 14.3501 10.1753 14.3501 10.3503V13.2003C14.3501 13.6128 14.2033 13.966 13.9098 14.26C13.6163 14.554 13.2631 14.7008 12.8501 14.7003H2.3501ZM6.5501 9.00029L3.94385 6.35654C3.65635 6.06904 3.5126 5.71279 3.5126 5.28779C3.5126 4.86279 3.65635 4.50654 3.94385 4.21904L7.61885 0.544043C7.90635 0.256543 8.2626 0.112793 8.6876 0.112793C9.1126 0.112793 9.46885 0.256543 9.75635 0.544043L12.4001 3.15029C12.6876 3.43779 12.8346 3.79079 12.8411 4.20929C12.8476 4.62779 12.7068 4.98104 12.4188 5.26904L8.66885 9.01904C8.38135 9.30654 8.02835 9.44729 7.60985 9.44129C7.19135 9.43529 6.8381 9.28829 6.5501 9.00029ZM11.3501 4.20029L8.6876 1.57529L4.9751 5.28779L7.6376 7.91279L11.3501 4.20029Z"
              fill="#000000"
            />
          </svg>
        </span>
        <span class="font-semibold text-lg text-gray-700">Schulze Method (Non-Positional):</span>
      </p>
      <p class="text-base text-gray-700 leading-relaxed">
        The Schulze method identifies the strongest paths between options based on pairwise
        comparisons. It evaluates the dominance of one option over another by considering all
        possible paths.
      </p>
      <p class="text-base text-gray-700 leading-relaxed mt-2">
        <span class="font-semibold">Key Steps:</span><br />
        1. Create a pairwise comparison matrix <code>P</code> for all options.<br />
        2. Calculate the path strength matrix <code>S</code> using the Floyd-Warshall algorithm:<br />
        <code>S[i][j] = max(S[i][j], min(S[i][k], S[k][j]))</code> (for all options
        <code>k</code>).<br />
        3. Rank options by their total dominance.
      </p>
      <p class="text-base text-gray-700 leading-relaxed mt-2">
        This method is particularly effective in contexts with complex or non-linear preferences.
      </p>
    </div>
  </div>
</div>
